import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { ru } from 'date-fns/locale';
import { operationsApi, holdersApi } from '../services/api';
import {
  formatCurrency,
  formatDate,
  getOperationTypeLabel,
  getStatusLabel,
  getStatusColor,
} from '../utils/formatters';
import { useApp } from '../App';

const Operations = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [holders, setHolders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    status: 'Pending',
    holder: '',
    startDate: null,
    endDate: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [opsRes, holdersRes] = await Promise.all([
        operationsApi.getAll({
          type: filters.type || undefined,
          status: filters.status || undefined,
          holder: filters.holder || undefined,
          startDate: filters.startDate?.toISOString().split('T')[0],
          endDate: filters.endDate?.toISOString().split('T')[0],
        }),
        holdersApi.getAll(),
      ]);

      setOperations(opsRes.data);
      setHolders(holdersRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки операций', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id) => {
    try {
      await operationsApi.approve(id);
      showNotification('Операция подтверждена', 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка подтверждения', 'error');
    }
  };

  const handleReject = async () => {
    try {
      await operationsApi.reject(rejectId, rejectReason);
      showNotification('Операция отклонена', 'success');
      setRejectDialogOpen(false);
      setRejectId(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      showNotification('Ошибка отклонения', 'error');
    }
  };

  const handleBulkApprove = async () => {
    if (selected.length === 0) {
      showNotification('Выберите операции', 'warning');
      return;
    }

    try {
      const result = await operationsApi.bulkApprove(selected);
      showNotification(
        `Подтверждено: ${result.data.summary.approved}, Ошибок: ${result.data.summary.failed}`,
        'success'
      );
      setSelected([]);
      fetchData();
    } catch (error) {
      showNotification('Ошибка массового подтверждения', 'error');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(operations.filter((o) => o.status === 'Pending').map((o) => o.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const pendingCount = operations.filter((o) => o.status === 'Pending').length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Операции
            </Typography>
            {pendingCount > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {pendingCount} операций ожидают подтверждения
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selected.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={handleBulkApprove}
              >
                Подтвердить выбранные ({selected.length})
              </Button>
            )}
            <Tooltip title="Обновить">
              <IconButton onClick={fetchData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Тип</InputLabel>
                  <Select
                    value={filters.type}
                    label="Тип"
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {['BUY', 'SELL', 'DEPOSIT', 'WITHDRAWAL', 'DIVIDEND', 'BLOCK', 'UNLOCK'].map(
                      (type) => (
                        <MenuItem key={type} value={type}>
                          {getOperationTypeLabel(type)}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={filters.status}
                    label="Статус"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {['Pending', 'Completed', 'Rejected'].map((status) => (
                      <MenuItem key={status} value={status}>
                        {getStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Владелец</InputLabel>
                  <Select
                    value={filters.holder}
                    label="Владелец"
                    onChange={(e) => setFilters({ ...filters, holder: e.target.value })}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {holders.map((h) => (
                      <MenuItem key={h.id} value={h.code}>
                        {h.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="С даты"
                  value={filters.startDate}
                  onChange={(date) => setFilters({ ...filters, startDate: date })}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="По дату"
                  value={filters.endDate}
                  onChange={(date) => setFilters({ ...filters, endDate: date })}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Operations Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selected.length > 0 && selected.length < pendingCount
                      }
                      checked={pendingCount > 0 && selected.length === pendingCount}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Актив</TableCell>
                  <TableCell>Владелец</TableCell>
                  <TableCell align="right">Количество</TableCell>
                  <TableCell align="right">Цена</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell width={120}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                ) : operations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Нет операций
                    </TableCell>
                  </TableRow>
                ) : (
                  operations.map((op) => (
                    <TableRow key={op.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(op.id)}
                          onChange={() => handleSelect(op.id)}
                          disabled={op.status !== 'Pending'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(op.date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getOperationTypeLabel(op.type)}
                          size="small"
                          color={
                            ['BUY', 'DEPOSIT', 'DIVIDEND'].includes(op.type)
                              ? 'success'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{op.Asset?.ticker || '-'}</TableCell>
                      <TableCell>
                        <Chip label={op.Holder?.code || '-'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {op.quantity?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {op.price?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(op.amount, op.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(op.status)}
                          size="small"
                          color={getStatusColor(op.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {op.status === 'Pending' && (
                          <>
                            <Tooltip title="Подтвердить">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(op.id)}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Отклонить">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setRejectId(op.id);
                                  setRejectDialogOpen(true);
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
          <DialogTitle>Отклонить операцию</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Причина отклонения"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Отмена</Button>
            <Button variant="contained" color="error" onClick={handleReject}>
              Отклонить
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Operations;
